import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, Pencil, ImagePlus, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "@/components/ImagePicker";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";

const emptyProduct = {
  title: "", description: "", price: 0, sku: "", color: "", size: "",
  category: "", image_path: null, tags: [], attributes: {}, location_ids: [],
  product_url: "",
};

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£", INR: "₹", JPY: "¥" };

function fmtPrice(n, currency = "EUR") {
  const sym = CURRENCY_SYMBOLS[currency] || "€";
  return `${sym}${Number(n || 0).toFixed(2)}`;
}

function ImageThumb({ path }) {
  const [err, setErr] = React.useState(false);
  React.useEffect(() => { setErr(false); }, [path]);
  if (!path || err) {
    return (
      <div className="w-10 h-10 bg-[#fafafa] border border-[#e5e5e5] flex items-center justify-center">
        <ImagePlus className="w-4 h-4 text-[#a1a1aa]" />
      </div>
    );
  }
  return (
    <img
      src={`${API}/files/${path}`}
      alt=""
      className="w-10 h-10 object-cover border border-[#e5e5e5]"
      onError={() => setErr(true)}
    />
  );
}

export default function Catalog() {
  const { user } = useAuth();
  const [currency, setCurrency] = useState("EUR");
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [busy, setBusy] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState({}); // id -> bool
  const [confirmDel, setConfirmDel] = useState(null); // {kind:'single'|'bulk', id?:string, count?:number}
  const csvRef = useRef(null);

  const load = (status = statusFilter) =>
    api.get(`/products?status=${status}`).then((r) => setItems(r.data));
  useEffect(() => { load(statusFilter); setSelected({}); /* clear selection on filter change */ }, [statusFilter]); // eslint-disable-line
  useEffect(() => { api.get("/brand").then((r) => setCurrency(r.data.currency || "EUR")); }, [user]);
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);

  const selectedIds = useMemo(() => items.filter((p) => selected[p.id]).map((p) => p.id), [items, selected]);
  const allChecked = items.length > 0 && selectedIds.length === items.length;
  const someChecked = selectedIds.length > 0 && selectedIds.length < items.length;

  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const n = {};
      items.forEach((p) => { n[p.id] = true; });
      setSelected(n);
    }
  };

  const openNew = () => {
    setEditing("new");
    setForm(emptyProduct);
    setTagsInput("");
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...emptyProduct, ...p });
    setTagsInput((p.tags || []).join(", "));
  };
  const close = () => setEditing(null);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      await load();
      toast.success("Product saved");
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Product deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDelete = async (ids) => {
    if (!ids || !ids.length) {
      toast.error("Nothing selected");
      return;
    }
    try {
      const { data } = await api.post("/products/bulk-delete", { ids });
      toast.success(`${data.deleted} deleted`);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDel) return;
    if (confirmDel.kind === "single") await remove(confirmDel.id);
    else await bulkDelete(confirmDel.ids || []);
    setConfirmDel(null);
  };

  const bulkArchive = async () => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/products/bulk-archive", { ids: selectedIds });
      toast.success(`${data.updated} archived`);
      setSelected({});
      await load();
    } catch {
      toast.error("Bulk archive failed");
    }
  };

  const bulkUnarchive = async () => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/products/bulk-unarchive", { ids: selectedIds });
      toast.success(`${data.updated} restored`);
      setSelected({});
      await load();
    } catch {
      toast.error("Restore failed");
    }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.inserted > 0) {
        toast.success(
          data.skipped > 0
            ? `${data.inserted} imported · ${data.skipped} skipped`
            : `${data.inserted} products imported`
        );
        if (data.merged_variants > 0) {
          toast.success(
            `${data.merged_variants} variant row${data.merged_variants === 1 ? "" : "s"} merged into their parent product (colors & sizes combined).`,
            { duration: 7000 }
          );
        }
        if (data.brand_currency_updated && data.detected_currency) {
          setCurrency(data.detected_currency);
          toast.success(`Brand currency set to ${data.detected_currency} (detected from CSV header)`, { duration: 5000 });
        }
        if (data.created_locations > 0) {
          api.get("/locations").then((r) => setLocations(r.data));
          toast.success(
            `${data.created_locations} new store${data.created_locations === 1 ? "" : "s"} created from the locations column — add addresses in Locations to enable distance.`,
            { duration: 7000 }
          );
        }
      } else {
        const detected = (data.detected_headers || []).join(", ") || "none";
        toast.error(
          `0 imported. Detected headers: [${detected}]. Need a "title" or "name" column.`,
          { duration: 8000 }
        );
      }
      await load();
    } catch {
      toast.error("CSV import failed");
    }
    e.target.value = "";
  };

  const FILTERS = [
    { id: "active", label: "Active" },
    { id: "archived", label: "Archived" },
    { id: "all", label: "All" },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        description="Add manually or import a CSV. EU prices like 19,99 are auto-converted. Archive items to remove them from the recommender without losing data."
      >
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={uploadCsv} data-testid="csv-input" />
        <Button
          variant="outline"
          onClick={() => csvRef.current?.click()}
          data-testid="csv-upload-button"
          className="rounded-none border-[#e5e5e5] h-11 px-5"
        >
          <Upload className="mr-2 w-4 h-4" /> Import CSV
        </Button>
        <Button
          onClick={openNew}
          data-testid="add-product-button"
          className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
        >
          <Plus className="mr-2 w-4 h-4" /> Add product
        </Button>
      </PageHeader>

      <div className="p-10">
        {/* Filter tabs + bulk bar */}
        <div className="bg-white border border-[#e5e5e5] border-b-0 px-4 py-3 flex items-center justify-between gap-4">
          <div className="inline-flex border border-[#e5e5e5]" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`px-4 py-1.5 text-xs label-overline transition-colors ${
                  statusFilter === f.id ? "bg-[#111] text-white" : "bg-white text-[#52525b] hover:bg-[#fafafa]"
                } ${f.id !== "active" ? "border-l border-[#e5e5e5]" : ""}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {selectedIds.length > 0 ? (
            <div className="flex items-center gap-2" data-testid="bulk-action-bar">
              <span className="label-overline text-[#52525b] mr-2">{selectedIds.length} selected</span>
              {statusFilter === "archived" ? (
                <Button
                  variant="outline"
                  onClick={bulkUnarchive}
                  data-testid="bulk-unarchive-button"
                  className="rounded-none border-[#e5e5e5] h-9"
                >
                  <ArchiveRestore className="mr-2 w-3.5 h-3.5" /> Restore
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={bulkArchive}
                  data-testid="bulk-archive-button"
                  className="rounded-none border-[#e5e5e5] h-9"
                >
                  <Archive className="mr-2 w-3.5 h-3.5" /> Archive
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setConfirmDel({ kind: "bulk", count: selectedIds.length, ids: [...selectedIds] })}
                data-testid="bulk-delete-button"
                className="rounded-none border-[#e63946] text-[#e63946] hover:bg-[#e63946] hover:text-white h-9"
              >
                <Trash2 className="mr-2 w-3.5 h-3.5" /> Delete
              </Button>
            </div>
          ) : (
            <span className="label-overline text-[#a1a1aa]">{items.length} item{items.length === 1 ? "" : "s"}</span>
          )}
        </div>

        <div className="bg-white border border-[#e5e5e5]">
          {/* Header row */}
          <div className="grid grid-cols-12 px-6 py-3 border-b border-[#e5e5e5] items-center">
            <div className="col-span-1">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                onChange={toggleAll}
                data-testid="select-all-checkbox"
                aria-label="Select all"
              />
            </div>
            <div className="col-span-4 label-overline">Product</div>
            <div className="col-span-2 label-overline">Price</div>
            <div className="col-span-2 label-overline">Color</div>
            <div className="col-span-2 label-overline">Category</div>
            <div className="col-span-1 label-overline text-right">Actions</div>
          </div>
          {items.length === 0 && (
            <div className="px-6 py-16 text-center text-sm text-[#52525b]" data-testid="empty-products">
              {statusFilter === "archived"
                ? "Nothing archived yet."
                : "No products yet. Add one or import a CSV with columns: title, description, price, sku, color, size, category, tags, locations."}
            </div>
          )}
          {items.map((p) => {
            const isChecked = !!selected[p.id];
            const isArchived = p.is_archived;
            return (
              <div
                key={p.id}
                className={`grid grid-cols-12 items-center px-6 py-4 border-b border-[#e5e5e5] last:border-b-0 transition-colors ${
                  isChecked ? "bg-[#f4f6ff]" : "hover:bg-[#fafafa]"
                } ${isArchived ? "opacity-60" : ""}`}
                data-testid={`product-row-${p.id}`}
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setSelected((s) => ({ ...s, [p.id]: !s[p.id] }))}
                    data-testid={`select-${p.id}`}
                    aria-label={`Select ${p.title}`}
                  />
                </div>
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <ImageThumb path={p.image_path} />
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {p.title}
                      {isArchived && (
                        <span className="label-overline border border-[#e5e5e5] px-1.5 py-0.5 text-[9px] text-[#52525b]">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#52525b] truncate font-mono">
                      {p.sku || "—"}
                      {(p.location_ids || []).length > 0 && (
                        <span className="ml-2 text-[#2f7d4f]">
                          · {p.location_ids.length} store{p.location_ids.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 font-mono text-sm">{fmtPrice(p.price, currency)}</div>
                <div className="col-span-2 text-sm">{p.color || "—"}</div>
                <div className="col-span-2 text-sm">{p.category || "—"}</div>
                <div className="col-span-1 flex justify-end gap-2">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-product-${p.id}`}
                    className="p-2 hover:bg-white border border-[#e5e5e5]"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setConfirmDel({ kind: "single", id: p.id })}
                    data-testid={`delete-product-${p.id}`}
                    className="p-2 hover:bg-white border border-[#e5e5e5] text-[#e63946]"
                    title="Delete product"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Sheet open={editing !== null} onOpenChange={(o) => !o && close()}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none border-l border-[#e5e5e5] flex flex-col p-0" data-testid="product-sheet">
          <SheetHeader className="shrink-0 p-6 pb-4 border-b border-[#e5e5e5]">
            <SheetTitle className="font-display font-bold tracking-tight text-2xl">
              {editing === "new" ? "Add product" : "Edit product"}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5" data-testid="product-sheet-scroll">
            <div>
              <Label className="label-overline">Image</Label>
              <div className="mt-2">
                <ImagePicker
                  path={form.image_path}
                  onChange={(p) => setForm((f) => ({ ...f, image_path: p }))}
                  size="lg"
                  testid="product-image"
                />
              </div>
            </div>
            <div>
              <Label className="label-overline">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="product-title"
              />
            </div>
            <div>
              <Label className="label-overline">Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="product-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-overline">Price ({CURRENCY_SYMBOLS[currency]})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-price"
                />
              </div>
              <div>
                <Label className="label-overline">SKU</Label>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-sku"
                />
              </div>
              <div>
                <Label className="label-overline">Color</Label>
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-color"
                />
              </div>
              <div>
                <Label className="label-overline">Size</Label>
                <Input
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-size"
                />
              </div>
              <div className="col-span-2">
                <Label className="label-overline">Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-category"
                />
              </div>
              <div className="col-span-2">
                <Label className="label-overline">Product URL</Label>
                <Input
                  type="url"
                  value={form.product_url || ""}
                  onChange={(e) => setForm({ ...form, product_url: e.target.value })}
                  placeholder="https://yourstore.com/products/malabar-teak-lounge-chair"
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-url"
                />
                <p className="mt-1 text-xs text-[#52525b]">
                  Optional. When set, the kiosk's <span className="font-medium">“I want this”</span> button opens this page in a new tab.
                </p>
              </div>
              <div className="col-span-2">
                <Label className="label-overline">Tags (comma-separated)</Label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="minimal, premium, summer"
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="product-tags"
                />
                <p className="mt-1 text-xs text-[#52525b]">
                  Tags help the AI match products with customer preferences.
                </p>
              </div>
              <div className="col-span-2">
                <Label className="label-overline">Available at (stores)</Label>
                {locations.length === 0 ? (
                  <p className="mt-2 text-xs text-[#52525b]" data-testid="no-locations-hint">
                    No stores defined yet.{" "}
                    <Link to="/dashboard/locations" className="underline text-[#0F1210]">
                      Add stores in Locations
                    </Link>{" "}
                    to show customers where this product is available.
                  </p>
                ) : (
                  <div className="mt-2 border border-[#e5e5e5] divide-y divide-[#e5e5e5] max-h-48 overflow-y-auto" data-testid="product-locations">
                    {locations.map((l) => {
                      const checked = (form.location_ids || []).includes(l.id);
                      return (
                        <label
                          key={l.id}
                          className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-[#fafafa]"
                          data-testid={`product-location-${l.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setForm((f) => ({
                                ...f,
                                location_ids: checked
                                  ? (f.location_ids || []).filter((id) => id !== l.id)
                                  : [...(f.location_ids || []), l.id],
                              }))
                            }
                          />
                          <span className="min-w-0">
                            <span className="font-medium">{l.name}</span>
                            {l.address && <span className="text-[#52525b]"> · {l.address}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="mt-1 text-xs text-[#52525b]">
                  The kiosk shows customers the closest store carrying their recommended product.
                </p>
              </div>
            </div>
          </div>

          <div className="shrink-0 bg-white border-t border-[#e5e5e5] p-6 flex justify-end gap-3">
            <Button variant="outline" onClick={close} className="rounded-none h-11 px-5 border-[#e5e5e5]">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="save-product-button"
              className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="rounded-none border border-[#e5e5e5]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-bold tracking-tight text-2xl">
              {confirmDel?.kind === "bulk"
                ? `Delete ${confirmDel?.count} product${confirmDel?.count === 1 ? "" : "s"}?`
                : "Delete this product?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent. To keep data, archive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-delete-cancel"
              className="rounded-none border-[#e5e5e5] h-11 px-5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-confirm"
              onClick={handleConfirmDelete}
              className="rounded-none bg-[#e63946] hover:bg-[#c1303d] text-white h-11 px-5"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
